import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';

/**
 * Shared data cache.
 *
 * Home, Ausflugsideen and the header search all read the same entity lists.
 * Routing every read through these hooks means one request per list per visit
 * session instead of one per page, and switching between Home and Ausflugsideen
 * no longer refetches the whole activity list.
 *
 * The query keys are the ones the pages already used, so their caches merge
 * instead of sitting next to each other. A visit loads each list once and then
 * navigates on cached data; the cache lives in memory only, so a reload and a
 * new tab still start from fresh data.
 */
const ACTIVITY_STALE = 1000 * 60 * 60 * 24; // edits happen in the admin views
const TAXONOMY_STALE = 1000 * 60 * 60 * 24; // categories, regions, seasons, labels
const WEATHER_STALE = 1000 * 60 * 60;
// Without this the default of five minutes would drop a list from memory long
// before its 24 hours are up, and the next navigation would load it again.
const LIST_GC = 1000 * 60 * 60 * 24;

export function useActivities() {
  return useQuery({
    queryKey: ['activities'],
    queryFn: () => base44.entities.Activity.filter({ published: true }),
    staleTime: ACTIVITY_STALE,
    gcTime: LIST_GC,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: ['categories'],
    queryFn: () => base44.entities.Category.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useRegions() {
  return useQuery({
    queryKey: ['regions'],
    queryFn: () => base44.entities.Region.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useSeasons() {
  return useQuery({
    queryKey: ['seasons'],
    queryFn: () => base44.entities.Season.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useLabels() {
  return useQuery({
    queryKey: ['labels'],
    queryFn: () => base44.entities.Label.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useDeals() {
  return useQuery({
    queryKey: ['deals'],
    queryFn: () => base44.entities.Deal.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useFeatures() {
  return useQuery({
    queryKey: ['features'],
    queryFn: () => base44.entities.Feature.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useFilterCategories() {
  return useQuery({
    queryKey: ['filterCategories'],
    queryFn: () => base44.entities.FilterCategory.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

export function useFilterOptions() {
  return useQuery({
    queryKey: ['filterOptions'],
    queryFn: () => base44.entities.FilterOption.filter({ is_active: true }),
    staleTime: TAXONOMY_STALE,
    gcTime: LIST_GC,
  });
}

/**
 * One weather request for the whole app.
 *
 * "What is it like right now" and "what does the coming week look like" are
 * answered by the same records, so the newest two weeks are loaded once and
 * both hooks below read their part out of that single cache entry.
 */
async function fetchWeatherBundle() {
  const results = await base44.entities.DailyWeather.list('-date', 14);
  const todayStr = new Date().toISOString().split('T')[0];
  const today = results.find(r => r.date === todayStr) || null;

  // Every loaded day keyed by its date, so the home section can read the
  // forecast of a picked day (tomorrow, the weekend) from the same request.
  const byDate = {};
  results.forEach(r => { if (r.date) byDate[r.date] = r; });

  const weekEnd = new Date();
  weekEnd.setDate(weekEnd.getDate() + 6);
  const weekEndStr = weekEnd.toISOString().split('T')[0];
  const weekDays = results.filter(r => r.date && r.date >= todayStr && r.date <= weekEndStr);

  let week = null;
  if (weekDays.length > 0) {
    const counts = { sunny: 0, cloudy: 0, rainy: 0, snowy: 0 };
    weekDays.forEach(d => { if (counts[d.condition] != null) counts[d.condition]++; });
    const order = ['rainy', 'snowy', 'sunny', 'cloudy'];
    let dominant = null;
    let max = -1;
    order.forEach(c => { if (counts[c] > max) { max = counts[c]; dominant = c; } });
    week = dominant ? { condition: dominant, counts } : null;
  }

  return { today, week, byDate };
}

const WEATHER_QUERY = { queryKey: ['dailyWeather'], queryFn: fetchWeatherBundle, staleTime: WEATHER_STALE };

/** Today's weather record, shared with the planner's filter panel. */
export function useTodayWeather() {
  return useQuery({ ...WEATHER_QUERY, select: (data) => data?.today ?? null });
}

/** Dominant weather condition of the coming seven days, or null. */
export function useWeekWeather() {
  return useQuery({ ...WEATHER_QUERY, select: (data) => data?.week ?? null });
}

const NO_FORECASTS = {};

/** Stored forecast per date (YYYY-MM-DD) — read by the home day picker. */
export function useWeatherByDate() {
  return useQuery({ ...WEATHER_QUERY, select: (data) => data?.byDate || NO_FORECASTS });
}